import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { T } from "../../theme";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(155,81,255,.4); }
  50%       { box-shadow: 0 0 0 8px rgba(155,81,255,0); }
`;

/* ════════ LAYOUT ════════ */
const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${T.bg};
`;

const Wrap = styled.main`
  flex: 1;
  max-width: 560px;
  margin: 0 auto;
  padding: clamp(40px, 7vw, 84px) clamp(16px, 4vw, 32px) 96px;
  width: 100%;
`;

/* ════════ HEADER ════════ */
const PageEyebrow = styled.p`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--purple);
  margin-bottom: 8px;
`;

const PageTitle = styled.h1`
  font-size: clamp(1.6rem, 3.5vw, 2.1rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  color: ${T.text};
  margin-bottom: 8px;
`;

const PageSub = styled.p`
  font-size: 0.88rem;
  color: ${T.muted};
  line-height: 1.65;
  margin-bottom: 36px;
  max-width: 420px;
`;

/* ════════ FORM CARD ════════ */
const Card = styled.div`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 20px;
  padding: 28px 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c3aed, #9B51FF, #c084fc);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 14px;
`;

const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${T.muted};
`;

const Input = styled.input`
  height: 44px;
  background: ${T.bg};
  border: 1px solid ${T.border};
  border-radius: 10px;
  padding: 0 14px;
  color: ${T.text};
  font-size: 0.88rem;
  font-family: inherit;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  width: 100%;

  &::placeholder { color: ${T.muted2}; }
  &:focus {
    border-color: var(--purple);
    box-shadow: 0 0 0 3px var(--purple-soft);
  }
`;

const Btn = styled.button`
  width: 100%;
  background: var(--purple);
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.92rem;
  padding: 13px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  margin-top: 4px;
  box-shadow: 0 4px 16px rgba(155,81,255,.28);
  transition: filter .14s, transform .12s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const ErrBox = styled.div`
  margin-top: 14px;
  padding: 12px 16px;
  background: rgba(239,68,68,.08);
  border: 1px solid rgba(239,68,68,.2);
  border-radius: 10px;
  font-size: 0.84rem;
  color: #f87171;
  line-height: 1.55;
`;

const OkBox = styled.div`
  margin-top: 20px;
  padding: 16px 18px;
  background: rgba(74,222,128,.08);
  border: 1px solid rgba(74,222,128,.22);
  border-radius: 12px;
  font-size: 0.86rem;
  color: #4ade80;
  line-height: 1.6;
`;

/* ════════ LGPD ════════ */
const LgpdRow = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LgpdNote = styled.p`
  font-size: 0.72rem;
  color: ${T.muted2};
  line-height: 1.5;
`;

const DangerBtn = styled.button`
  align-self: flex-start;
  background: none;
  border: 1px solid rgba(239,68,68,.35);
  color: #f87171;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 9px 14px;
  border-radius: 9px;
  cursor: pointer;
  font-family: inherit;
  transition: background .12s, border-color .12s;
  &:hover:not(:disabled) { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.55); }
  &:disabled { opacity: 0.5; cursor: progress; }
`;

/* ════════ RESULT ════════ */
const STATUS_META = {
  recebido:   { label: "Recebido",        color: "#c4b5fd", bg: "rgba(155,81,255,.12)", border: "rgba(155,81,255,.3)",  dot: "#9B51FF" },
  em_analise: { label: "Em análise",      color: "#fbbf24", bg: "rgba(234,179,8,.1)",   border: "rgba(234,179,8,.28)",  dot: "#F59E0B" },
  aprovado:   { label: "Aprovado",        color: "#4ade80", bg: "rgba(74,222,128,.1)",  border: "rgba(74,222,128,.28)", dot: "#22c55e" },
  reprovado:  { label: "Não selecionado", color: "#f87171", bg: "rgba(239,68,68,.1)",   border: "rgba(239,68,68,.22)",  dot: "#ef4444" },
  desistiu:   { label: "Desistência",     color: "#94a3b8", bg: "rgba(100,116,139,.1)", border: "rgba(100,116,139,.22)",dot: "#64748b" },
};

const ResultWrap = styled.div`
  margin-top: 20px;
  animation: ${fadeUp} 0.35s ease both;
`;

const StatusCard = styled.div`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 18px;
  overflow: hidden;
`;

const StatusTop = styled.div`
  background: ${(p) => (STATUS_META[p.$s] || STATUS_META.recebido).bg};
  border-bottom: 1px solid ${(p) => (STATUS_META[p.$s] || STATUS_META.recebido).border};
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StatusLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${(p) => (STATUS_META[p.$s] || STATUS_META.recebido).color};
  opacity: .7;
`;

const StatusText = styled.p`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${(p) => (STATUS_META[p.$s] || STATUS_META.recebido).color};
  letter-spacing: -0.01em;
`;

const StatusDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${(p) => (STATUS_META[p.$s] || STATUS_META.recebido).dot};
  flex-shrink: 0;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const InfoList = styled.div`
  padding: 8px 24px 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${T.border};

  &:last-child { border-bottom: none; padding-bottom: 0; }
`;

const InfoKey = styled.span`
  font-size: 0.79rem;
  color: ${T.muted2};
  flex-shrink: 0;
`;

const InfoVal = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${T.text};
  text-align: right;
`;

/* timeline de etapas */
const STAGES = [
  { key: ["recebido", "em_analise", "aprovado", "reprovado", "desistiu"], label: "Candidatura recebida" },
  { key: ["em_analise", "aprovado", "reprovado"],                         label: "Em análise" },
  { key: ["aprovado"],                                                     label: "Aprovado para entrevista" },
];

const TimelineWrap = styled.div`
  margin-top: 16px;
  padding: 0 24px 20px;
  border-top: 1px solid ${T.border};
`;

const TimelineLabel = styled.p`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${T.muted2};
  margin: 16px 0 12px;
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const TDot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const TCircle = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${(p) => p.$done ? "var(--purple)" : T.border};
  background: ${(p) => p.$done ? "var(--purple)" : T.card};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  color: #fff;
  font-weight: 800;
  transition: all .2s;
`;

const TLine = styled.div`
  width: 2px;
  height: 24px;
  background: ${(p) => p.$done ? "rgba(155,81,255,.4)" : T.border};
  flex-shrink: 0;
`;

const TText = styled.p`
  font-size: 0.82rem;
  color: ${(p) => p.$done ? T.text : T.muted2};
  font-weight: ${(p) => p.$done ? "600" : "400"};
  padding-top: 0;
  line-height: 18px;
`;

/* ══════════════════════════════════
   COMPONENT
══════════════════════════════════ */
const API = process.env.REACT_APP_CAREERS_API || "https://api.carrers.guebly.com.br";

export default function Acompanhar() {
  const [email,    setEmail]    = useState("");
  const [code,     setCode]     = useState("");
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleted,  setDeleted]  = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email || !code) return;
    setLoading(true);
    setErr("");
    setResult(null);
    setDeleted(false);

    try {
      const res = await fetch(
        `${API}/api/applications/track?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`
      );
      if (!res.ok) throw new Error("Candidatura não encontrada.");
      const data = await res.json();
      setResult(data.data);
    } catch (e) {
      setErr(e.message || "Não encontramos sua candidatura. Verifique o e-mail e o código.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Tem certeza? Isso apaga PERMANENTEMENTE sua candidatura e o currículo enviado. Não dá pra desfazer.")) return;
    setDeleting(true);
    setErr("");
    try {
      const res = await fetch(`${API}/api/applications/delete-my-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      if (!res.ok) throw new Error();
      setResult(null);
      setDeleted(true);
    } catch {
      setErr("Não foi possível excluir agora. Tente novamente em instantes.");
    } finally {
      setDeleting(false);
    }
  };

  const s = result?.status;
  const meta = STATUS_META[s] || STATUS_META.recebido;

  return (
    <Page>
      <Header />
      <Wrap id="main-content">
        <PageEyebrow aria-hidden="true">Candidatura</PageEyebrow>
        <PageTitle>Acompanhar status</PageTitle>
        <PageSub>
          Acompanhe sua candidatura com o e-mail cadastrado e o código
          que enviamos logo após o envio.
        </PageSub>

        <Card>
          <form onSubmit={handleSearch}>
            <Field>
              <Label htmlFor="track-email">E-mail cadastrado</Label>
              <Input
                id="track-email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-required="true"
              />
            </Field>
            <Field>
              <Label htmlFor="track-code">Código de acompanhamento</Label>
              <Input
                id="track-code"
                placeholder="GUEBLY-XXXX-XXXX"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                required
                aria-required="true"
              />
            </Field>
            <Btn type="submit" disabled={loading}>
              {loading ? "Buscando…" : "Verificar status →"}
            </Btn>
          </form>

          <div aria-live="polite" aria-atomic="true">
            {err && <ErrBox role="alert">{err}</ErrBox>}
          </div>
        </Card>

        {deleted && (
          <OkBox role="status">
            <strong>Dados excluídos.</strong> Sua candidatura e o currículo enviado
            foram apagados dos nossos sistemas, conforme a LGPD. Se quiser, você pode
            se candidatar novamente a qualquer momento.
          </OkBox>
        )}

        {result && (
          <ResultWrap>
            <StatusCard>
              <StatusTop $s={s}>
                <StatusLeft>
                  <StatusLabel $s={s}>Status atual</StatusLabel>
                  <StatusText $s={s}>{meta.label}</StatusText>
                </StatusLeft>
                <StatusDot $s={s} aria-hidden="true" />
              </StatusTop>

              <InfoList>
                <InfoRow>
                  <InfoKey>Vaga</InfoKey>
                  <InfoVal>{result.job_title}</InfoVal>
                </InfoRow>
                <InfoRow>
                  <InfoKey>Empresa</InfoKey>
                  <InfoVal>Guebly Holding</InfoVal>
                </InfoRow>
                <InfoRow>
                  <InfoKey>Candidatura em</InfoKey>
                  <InfoVal>
                    {new Date(result.created_at).toLocaleDateString("pt-BR", {
                      day: "2-digit", month: "long", year: "numeric",
                    })}
                  </InfoVal>
                </InfoRow>
              </InfoList>

              <TimelineWrap>
                <TimelineLabel>Etapas</TimelineLabel>
                <TimelineList>
                  {STAGES.map((stage, i) => {
                    const done = stage.key.includes(s);
                    return (
                      <TimelineItem key={i}>
                        <TDot>
                          <TCircle $done={done} aria-hidden="true">{done ? "✓" : ""}</TCircle>
                          {i < STAGES.length - 1 && <TLine $done={done} />}
                        </TDot>
                        <TText $done={done}>{stage.label}</TText>
                      </TimelineItem>
                    );
                  })}
                </TimelineList>
              </TimelineWrap>
            </StatusCard>

            <LgpdRow>
              <LgpdNote>
                De acordo com a LGPD, você pode solicitar a exclusão dos seus dados
                a qualquer momento. Isso apaga sua candidatura e o currículo enviado.
              </LgpdNote>
              <DangerBtn onClick={handleDelete} disabled={deleting}>
                {deleting ? "Excluindo…" : "Excluir meus dados (LGPD)"}
              </DangerBtn>
            </LgpdRow>
          </ResultWrap>
        )}
      </Wrap>
      <Footer />
    </Page>
  );
}
